import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { Command as CommandPrimitive } from 'cmdk';
import {
  ArrowUpRight,
  BookMarked,
  ChevronDown,
  Grid3x3,
  List,
  Search,
  Star,
  X,
} from 'lucide-react';
import { Kicker, SmchsLayout } from '@/components/smchs/Shell';
import {
  BookCover,
  CollectionStyle,
  GrainOverlay,
  Reveal,
} from '@/components/smchs/Collection';
import { useCatalogMeta } from '@/hooks/useCatalogMeta';
import { useCatalogBooks } from '@/hooks/useCatalogBooks';
import { useDebounce } from '@/hooks/use-debounce';
import { useBookSuggestionsBox, type BookSuggestionsBox, type Suggestion } from '@/hooks/use-book-suggestions-box';
import { resolveCoverImageUrl } from '@/lib/cover-image';
import { booksApi, Book, Genre, BookLibrarianStats, type AuthorSuggestion } from '@/services/catalog-api';
import { loansApi } from '@/services/loans-api';
import { TokenManager } from '@/lib/token-manager';
import { authorLine } from '@/components/smchs/collection-lib';

/* =====================================================================
 * SMCHS Library: catalog, in the "living collection" editorial
 * vocabulary (see components/smchs/Collection.tsx for the shared
 * primitives and the radius/label rules).
 *
 * All wiring is preserved from the previous version: debounced live
 * search seeded from ?search=, genre chip windowing with a select
 * overflow, infinite scroll via IntersectionObserver, and the reserve
 * flow in the detail drawer.
 * ===================================================================== */

type SortKey = 'relevance' | 'newest' | 'oldest' | 'rating' | 'title';

// "Best match" is offered only while searching; without a search it would just be title order.
const sortOptions: { value: SortKey; label: string }[] = [
  { value: 'relevance', label: 'Best match' },
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'rating', label: 'Highest rated' },
  { value: 'title', label: 'Title A to Z' },
];

const GENRE_CHIP_LIMIT = 4;

const SORT_TO_ORDERING: Record<SortKey, string> = {
  relevance: 'relevance',
  newest: '-publication_date',
  oldest: 'publication_date',
  rating: '-average_rating',
  title: 'title',
};

const Catalog: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('search') || '');
  const [activeGenreId, setActiveGenreId] = useState<number | null>(null);
  const [availableOnly, setAvailableOnly] = useState(false);
  const [author, setAuthor] = useState<AuthorSuggestion | null>(null);
  // null until the reader picks a sort: then it is Best match while searching, else Newest.
  const [sort, setSort] = useState<SortKey | null>(null);
  const [view, setView] = useState<'grid' | 'list'>('grid');
  const [selectedBookId, setSelectedBookId] = useState<number | null>(null);

  const [debouncedQuery] = useDebounce(query, 350);
  // Follows the debounced query, so a keystroke never fetches with the wrong order first.
  const searching = debouncedQuery.trim() !== '';
  const effectiveSort: SortKey =
    sort === null || (sort === 'relevance' && !searching) ? (searching ? 'relevance' : 'newest') : sort;

  const { stats, genres, isLoading: metaLoading } = useCatalogMeta();

  const { books, isFetching, hasMore, error, loadMore, retry } = useCatalogBooks({
    query: debouncedQuery,
    genreId: activeGenreId,
    authorId: author?.id ?? null,
    availableOnly,
    ordering: SORT_TO_ORDERING[effectiveSort],
  });

  // Sentinel ref for IntersectionObserver (infinite scroll)
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) loadMore(); },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore]);

  const hasFilters = !!query || activeGenreId !== null || author !== null || availableOnly || sort !== null;

  const reset = () => {
    setQuery('');
    setActiveGenreId(null);
    setAuthor(null);
    setAvailableOnly(false);
    setSort(null);
  };

  return (
    <SmchsLayout>
      <CollectionStyle />
      <CatalogHero
        query={query}
        setQuery={setQuery}
        stats={stats}
        onPick={(picked) => {
          if (picked.kind === 'book') {
            setSelectedBookId(picked.book.id);
          } else {
            setAuthor(picked.author);
            setQuery('');
          }
        }}
      />
      <StatsBand stats={stats} isLoading={metaLoading} />
      <FilterBar
        genres={genres}
        activeGenreId={activeGenreId}
        setActiveGenreId={setActiveGenreId}
        author={author}
        clearAuthor={() => setAuthor(null)}
        availableOnly={availableOnly}
        setAvailableOnly={setAvailableOnly}
        sort={effectiveSort}
        setSort={setSort}
        searching={searching}
        view={view}
        setView={setView}
        count={books.length}
        hasFilters={hasFilters}
        reset={reset}
        isLoading={metaLoading}
      />
      <BooksSection
        books={books}
        view={view}
        reset={reset}
        isFetching={isFetching}
        error={error}
        retry={retry}
        sentinelRef={sentinelRef}
        onBookClick={setSelectedBookId}
      />
      <BookDetailDrawer bookId={selectedBookId} onClose={() => setSelectedBookId(null)} />
      <GrainOverlay />
    </SmchsLayout>
  );
};

/* =====================================================================
 * HERO: compact for a working page, since the search is the point.
 * ===================================================================== */

const CatalogHero: React.FC<{
  query: string;
  setQuery: (s: string) => void;
  stats: BookLibrarianStats | null;
  onPick: (picked: Suggestion) => void;
}> = ({ query, setQuery, stats, onPick }) => {
  const titleCount = stats?.total_books;
  const box = useBookSuggestionsBox({ text: query, onPick });
  return (
    // No overflow-hidden: the suggestions list hangs below the section.
    <section className="relative border-b border-[var(--border)]">
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_70%_55%_at_28%_0%,rgba(30,45,92,0.08),transparent_65%)]" />
      <div className="max-w-[1280px] mx-auto px-5 md:px-8 pt-12 md:pt-16 pb-10 md:pb-12">
        <Kicker label="The collection" />
        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: 'easeOut' }}
          className="mt-5 font-display font-[900] text-[2.4rem] sm:text-[3.2rem] md:text-[4rem] leading-[1.05] tracking-[-0.03em] text-[var(--ink)] pb-1"
        >
          The <span className="font-serif italic font-normal text-[var(--primary)]">whole</span>{' '}
          collection.
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.12, ease: 'easeOut' }}
          className="mt-4 max-w-[52ch] text-[1rem] md:text-[1.05rem] leading-[1.55] text-[var(--ink-soft)]"
        >
          {titleCount
            ? `Search ${titleCount.toLocaleString()} titles, filter by genre, and see what's on the shelf right now.`
            : "Search the shelves, filter by genre, and see what's available right now."}
        </motion.p>

        <motion.form
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.24, ease: 'easeOut' }}
          onSubmit={(e) => {
            e.preventDefault();
            box.close(); // the results grid below already shows the search
          }}
          className="relative z-20 mt-8 max-w-[620px]"
        >
          <div className="rounded-lg bg-[var(--surface)] border border-[var(--border)] p-1.5 flex items-center gap-1.5 focus-within:border-[var(--gold)] transition-colors shadow-[0_16px_36px_-24px_rgba(20,24,43,0.35)]">
            <Search className="w-5 h-5 ml-3 text-[var(--muted)] shrink-0" strokeWidth={1.75} />
            <input
              {...box.inputProps}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                box.reopen();
              }}
              aria-label="Search the catalog"
              placeholder="Search by title, author, or keyword"
              autoComplete="off"
              className="flex-1 min-w-0 bg-transparent outline-none px-2 py-3 text-[15px] placeholder:text-[var(--muted)]"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                className="shrink-0 w-9 h-9 rounded-lg flex items-center justify-center text-[var(--muted)] hover:text-[var(--ink)] hover:bg-[var(--surface-2)] transition-colors"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" strokeWidth={1.75} />
              </button>
            )}
          </div>
          <SuggestionList box={box} />
        </motion.form>
      </div>
    </section>
  );
};

/* =====================================================================
 * STATS BAND: ledger numerals between hairlines. Hidden when the API
 * has nothing to say; zeros would be a lie.
 * ===================================================================== */

const StatsBand: React.FC<{
  stats: BookLibrarianStats | null;
  isLoading: boolean;
}> = ({ stats, isLoading }) => {
  if (!isLoading && !stats) return null;

  const tiles = [
    { label: 'Titles', value: stats?.total_books },
    { label: 'Available now', value: stats?.available_count },
    { label: 'Authors', value: stats?.total_authors },
    { label: 'Genres', value: stats?.total_genres },
  ];

  return (
    <section className="border-b border-[var(--border)]">
      <div className="max-w-[1280px] mx-auto px-5 md:px-8 grid grid-cols-2 md:grid-cols-4">
        {tiles.map((s, i) => (
          <Reveal
            key={s.label}
            delay={i * 0.05}
            className={`py-6 md:py-8 ${i > 0 ? 'md:border-l md:border-[var(--border)] md:pl-8' : ''} ${
              i % 2 === 1 ? 'border-l border-[var(--border)] pl-6 md:pl-8' : ''
            } ${i > 1 ? 'border-t border-[var(--border)] md:border-t-0' : ''}`}
          >
            {isLoading || s.value === undefined ? (
              <div className="h-9 w-20 rounded bg-[var(--surface-2)] animate-pulse" />
            ) : (
              <div className="font-display font-[900] text-[1.9rem] md:text-[2.4rem] leading-none tracking-[-0.03em] tabular-nums text-[var(--ink)]">
                {s.value.toLocaleString()}
              </div>
            )}
            <div className="mt-2 text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--muted)]">
              {s.label}
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

/* =====================================================================
 * FILTER BAR: sticky working surface. Logic unchanged.
 * ===================================================================== */

/* SUGGESTIONS: the list under the hero search box. Rendered in place (no portal),
   because the SMCHS variables are scoped to .smchs-home. */
const SuggestionList: React.FC<{ box: BookSuggestionsBox }> = ({ box }) => {
  const count = box.items.length;
  return (
    <>
      <p aria-live="polite" className="sr-only">
        {box.shown ? `${count} suggestion${count === 1 ? '' : 's'}` : ''}
      </p>
      {box.shown && (
        <div
          {...box.listProps}
          aria-label="Suggestions"
          className="absolute left-0 right-0 top-full mt-2 max-h-[70vh] overflow-y-auto rounded-lg bg-[var(--surface)] border border-[var(--border)] shadow-[0_20px_40px_-24px_rgba(20,24,43,0.45)] py-1.5"
        >
          {box.books.length > 0 && (
            <div role="group" aria-label="Books">
              <p aria-hidden className="px-4 pt-1.5 pb-1 text-[11px] uppercase tracking-[0.12em] text-[var(--muted)]">Books</p>
              {box.books.map((book, i) => {
                const cover = resolveCoverImageUrl(book.cover_image);
                return (
                  <div
                    key={`book-${book.id}`}
                    {...box.optionProps(i)}
                    className={`flex items-center gap-3 px-4 py-2 cursor-pointer ${box.active === i ? 'bg-[var(--surface-2)]' : ''}`}
                  >
                    {cover ? (
                      <img src={cover} alt="" className="w-7 h-10 rounded-sm object-cover shrink-0 border border-[var(--border)]" />
                    ) : (
                      <span aria-hidden className="w-7 h-10 rounded-sm shrink-0 bg-[var(--surface-2)] border border-[var(--border)]" />
                    )}
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[14px] text-[var(--ink)]">{book.title}</span>
                      <span className="block truncate text-[12px] text-[var(--ink-soft)]">
                        {[book.author_names[0], book.publication_year].filter(Boolean).join(' · ')}
                      </span>
                    </span>
                    <span className={`shrink-0 text-[11.5px] ${book.available_copies > 0 ? 'text-[var(--primary)]' : 'text-[var(--muted)]'}`}>
                      {book.available_copies > 0 ? 'On the shelf' : 'All out'}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          {box.authors.length > 0 && (
            <div role="group" aria-label="Authors">
              <p aria-hidden className="px-4 pt-2 pb-1 text-[11px] uppercase tracking-[0.12em] text-[var(--muted)]">Authors</p>
              {box.authors.map((author, j) => {
                const i = box.books.length + j;
                return (
                  <div
                    key={`author-${author.id}`}
                    {...box.optionProps(i)}
                    className={`flex items-center justify-between gap-3 px-4 py-2 cursor-pointer ${box.active === i ? 'bg-[var(--surface-2)]' : ''}`}
                  >
                    <span className="truncate text-[14px] text-[var(--ink)]">{author.name}</span>
                    <span className="shrink-0 text-[12px] text-[var(--muted)]">
                      {author.book_count} {author.book_count === 1 ? 'book' : 'books'}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      {box.open && box.empty && (
        <p className="absolute left-0 right-0 top-full mt-2 rounded-lg bg-[var(--surface)] border border-[var(--border)] px-4 py-3 text-[13px] text-[var(--ink-soft)]">
          No matches for "{box.query}"
        </p>
      )}
    </>
  );
};

interface FilterBarProps {
  genres: Genre[];
  activeGenreId: number | null;
  setActiveGenreId: (v: number | null) => void;
  author: AuthorSuggestion | null;
  clearAuthor: () => void;
  availableOnly: boolean;
  setAvailableOnly: (v: boolean) => void;
  sort: SortKey;
  setSort: (v: SortKey) => void;
  searching: boolean;
  view: 'grid' | 'list';
  setView: (v: 'grid' | 'list') => void;
  count: number;
  hasFilters: boolean;
  reset: () => void;
  isLoading: boolean;
}

const chipClass = (active: boolean) =>
  `shrink-0 px-3.5 py-1.5 rounded-full text-[12.5px] font-medium border transition-all active:translate-y-px ${
    active
      ? 'bg-[var(--ink)] text-[var(--paper)] border-[var(--ink)]'
      : 'bg-[var(--surface)] text-[var(--ink-soft)] border-[var(--border)] hover:border-[var(--gold)] hover:text-[var(--ink)]'
  }`;

const FilterBar: React.FC<FilterBarProps> = ({
  genres, activeGenreId, setActiveGenreId, author, clearAuthor, availableOnly, setAvailableOnly, sort, setSort, searching, view, setView, count, hasFilters, reset, isLoading,
}) => {
  const activeGenre = useMemo(
    () => genres.find((genre) => genre.id === activeGenreId) ?? null,
    [activeGenreId, genres]
  );

  /* Only genres that can actually return results are offered as filters;
     an empty genre could only ever produce "no results". */
  const pickableGenres = useMemo(() => {
    const sorted = [...genres].sort((a, b) => {
      const countDelta = (b.book_count ?? 0) - (a.book_count ?? 0);
      return countDelta || a.name.localeCompare(b.name);
    });
    const withBooks = sorted.filter((genre) => (genre.book_count ?? 0) > 0);
    return withBooks.length > 0 ? withBooks : sorted;
  }, [genres]);

  const chipGenres = pickableGenres.slice(0, GENRE_CHIP_LIMIT);
  const activeInChips = activeGenreId === null || chipGenres.some((g) => g.id === activeGenreId);

  return (
    <section className="sticky top-[68px] z-30 bg-[var(--bg)]/90 backdrop-blur-md border-b border-[var(--border)]">
      <div className="max-w-[1280px] mx-auto px-5 md:px-8 py-4 flex flex-wrap items-center gap-3">
        {/* Genre filter: quick chips for the big genres, searchable picker for the rest */}
        <div className="flex items-center gap-1.5 flex-1 min-w-[min(100%,420px)]">
          {isLoading ? (
            <>
              {[64, 72, 56, 68].map((w, i) => (
                <div key={i} className="shrink-0 h-8 rounded-full bg-[var(--surface-2)] animate-pulse" style={{ width: w }} />
              ))}
            </>
          ) : (
            <>
              <div className="flex flex-wrap sm:flex-nowrap items-center gap-1.5 sm:overflow-x-auto sm:pr-6 scrollbar-none min-w-0 sm:[mask-image:linear-gradient(to_right,black_calc(100%-24px),transparent)]">
                <button onClick={() => setActiveGenreId(null)} className={chipClass(activeGenreId === null)}>
                  All
                </button>
                {chipGenres.map((g) => (
                  <button key={g.id} onClick={() => setActiveGenreId(g.id)} className={chipClass(activeGenreId === g.id)}>
                    {g.name}
                  </button>
                ))}
              </div>
              {pickableGenres.length > GENRE_CHIP_LIMIT && (
                <GenrePicker
                  genres={pickableGenres}
                  activeGenre={activeGenre}
                  isActive={!activeInChips}
                  onPick={setActiveGenreId}
                />
              )}
            </>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Author picked from the search suggestions */}
          {author && (
            <span className="flex items-center gap-1.5 pl-3.5 pr-1.5 py-1 rounded-full border border-[var(--gold)]/50 bg-[var(--gold)]/15 text-[12.5px] font-medium text-[var(--ink)]">
              Author: {author.name}
              <button
                type="button"
                onClick={clearAuthor}
                aria-label="Remove author filter"
                className="w-5 h-5 rounded-full flex items-center justify-center text-[var(--muted)] hover:text-[var(--ink)] hover:bg-[var(--surface-2)]"
              >
                <X className="w-3 h-3" strokeWidth={2} />
              </button>
            </span>
          )}

          {/* Available toggle */}
          <button
            onClick={() => setAvailableOnly(!availableOnly)}
            aria-pressed={availableOnly}
            className={`flex items-center gap-2 px-3.5 py-1.5 rounded-full border text-[12.5px] font-medium transition-all active:translate-y-px ${
              availableOnly
                ? 'bg-[var(--gold)]/15 text-[var(--ink)] border-[var(--gold)]/50'
                : 'bg-[var(--surface)] text-[var(--ink-soft)] border-[var(--border)] hover:border-[var(--gold)]'
            }`}
          >
            <span className={`w-2 h-2 rounded-full ${availableOnly ? 'bg-[var(--gold)]' : 'bg-[var(--muted)]/50'}`} />
            On the shelf only
          </button>

          {/* Sort */}
          <div className="relative">
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value as SortKey)}
              aria-label="Sort by"
              className="appearance-none pl-3.5 pr-8 py-1.5 rounded-full border border-[var(--border)] bg-[var(--surface)] text-[12.5px] font-medium text-[var(--ink-soft)] hover:border-[var(--gold)] cursor-pointer focus:outline-none focus:border-[var(--gold)]"
            >
              {sortOptions
                .filter((o) => searching || o.value !== 'relevance')
                .map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
            </select>
            <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[var(--muted)] pointer-events-none" strokeWidth={1.75} />
          </div>

          {/* View toggle */}
          <div className="flex items-center p-0.5 rounded-full border border-[var(--border)] bg-[var(--surface)]">
            <button
              onClick={() => setView('grid')}
              aria-pressed={view === 'grid'}
              className={`p-1.5 rounded-full transition-colors ${view === 'grid' ? 'bg-[var(--ink)] text-[var(--paper)]' : 'text-[var(--muted)] hover:text-[var(--ink)]'}`}
              aria-label="Grid view"
            >
              <Grid3x3 className="w-3.5 h-3.5" strokeWidth={1.75} />
            </button>
            <button
              onClick={() => setView('list')}
              aria-pressed={view === 'list'}
              className={`p-1.5 rounded-full transition-colors ${view === 'list' ? 'bg-[var(--ink)] text-[var(--paper)]' : 'text-[var(--muted)] hover:text-[var(--ink)]'}`}
              aria-label="List view"
            >
              <List className="w-3.5 h-3.5" strokeWidth={1.75} />
            </button>
          </div>
        </div>
      </div>

      {/* Count line */}
      <div className="max-w-[1280px] mx-auto px-5 md:px-8 pb-3 flex items-center justify-between text-[12px] font-mono uppercase tracking-[0.14em] text-[var(--muted)]">
        <span>
          Showing <span className="text-[var(--ink)] font-semibold tabular-nums">{count}</span> {count === 1 ? 'title' : 'titles'}
        </span>
        {hasFilters && (
          <button onClick={reset} className="text-[var(--ink-soft)] hover:text-[var(--ink)] inline-flex items-center gap-1 transition-colors">
            <X className="w-3 h-3" strokeWidth={1.75} /> Clear filters
          </button>
        )}
      </div>
    </section>
  );
};

/* =====================================================================
 * GENRE PICKER: searchable popover for the long tail. Rendered without
 * a portal on purpose: the SMCHS CSS vars are scoped to .smchs-home and
 * would not resolve at document.body.
 * ===================================================================== */

const GenrePicker: React.FC<{
  genres: Genre[];
  activeGenre: Genre | null;
  isActive: boolean;
  onPick: (id: number | null) => void;
}> = ({ genres, activeGenre, isActive, onPick }) => {
  const [open, setOpen] = useState(false);
  const label = isActive && activeGenre ? activeGenre.name : 'More genres';

  const pick = (id: number | null) => {
    onPick(id);
    setOpen(false);
  };

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger asChild>
        <button
          aria-label="Filter by genre"
          className={`${chipClass(isActive)} inline-flex items-center gap-1.5 max-w-[190px]`}
        >
          <span className="truncate">{label}</span>
          <ChevronDown
            className={`w-3.5 h-3.5 shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
            strokeWidth={1.75}
          />
        </button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Content
        align="start"
        sideOffset={8}
        collisionPadding={16}
        className="z-40 w-[280px] rounded-md border border-[var(--border)] bg-[var(--surface)] shadow-[0_28px_56px_-28px_rgba(10,18,40,0.4)] overflow-hidden outline-none"
      >
        <CommandPrimitive loop className="outline-none">
          <div className="flex items-center gap-2 border-b border-[var(--border)] px-3">
            <Search className="w-3.5 h-3.5 text-[var(--muted)] shrink-0" strokeWidth={1.75} />
            <CommandPrimitive.Input
              autoFocus
              placeholder="Search genres"
              className="w-full bg-transparent py-2.5 text-[13px] text-[var(--ink)] outline-none placeholder:text-[var(--muted)]"
            />
          </div>
          <CommandPrimitive.List className="max-h-[280px] overflow-y-auto p-1.5">
            <CommandPrimitive.Empty className="py-6 text-center text-[12.5px] text-[var(--muted)]">
              No genre matches.
            </CommandPrimitive.Empty>
            <GenreRow
              value="all genres"
              name="All genres"
              selected={activeGenre === null}
              onSelect={() => pick(null)}
            />
            {genres.map((g) => (
              <GenreRow
                key={g.id}
                value={g.name}
                name={g.name}
                count={g.book_count}
                selected={activeGenre?.id === g.id}
                onSelect={() => pick(g.id)}
              />
            ))}
          </CommandPrimitive.List>
        </CommandPrimitive>
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Root>
  );
};

const GenreRow: React.FC<{
  value: string;
  name: string;
  count?: number;
  selected: boolean;
  onSelect: () => void;
}> = ({ value, name, count, selected, onSelect }) => (
  <CommandPrimitive.Item
    value={value}
    onSelect={onSelect}
    className="flex items-center justify-between gap-3 px-2.5 py-2 rounded cursor-pointer text-[13px] text-[var(--ink-soft)] data-[selected=true]:bg-[var(--surface-2)] data-[selected=true]:text-[var(--ink)]"
  >
    <span className="flex items-center gap-2 min-w-0">
      <span
        className={`w-1.5 h-1.5 rounded-full shrink-0 ${selected ? 'bg-[var(--gold)]' : 'bg-transparent'}`}
      />
      <span className={`truncate ${selected ? 'font-medium text-[var(--ink)]' : ''}`}>{name}</span>
    </span>
    {typeof count === 'number' && (
      <span className="font-mono text-[11px] tabular-nums text-[var(--muted)] shrink-0">{count}</span>
    )}
  </CommandPrimitive.Item>
);

/* =====================================================================
 * AVAILABILITY: dot plus words, never colour alone.
 * ===================================================================== */

const AvailabilityBadge: React.FC<{ available: number; total: number; long?: boolean }> = ({
  available,
  total,
  long = false,
}) => {
  const anyIn = available > 0;
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap font-mono text-[11px] uppercase tracking-[0.1em] tabular-nums ${
        anyIn ? 'text-[var(--ink-soft)]' : 'text-[var(--muted)]'
      }`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${anyIn ? 'bg-[var(--gold)]' : 'border border-[var(--muted)]'}`} />
      {long ? `${available} of ${total} in` : anyIn ? `${available} in` : 'All out'}
    </span>
  );
};

/* =====================================================================
 * BOOKS SECTION: states preserved (error, initial skeleton, empty,
 * grid/list, infinite-scroll sentinel, next-page indicator.
 * ===================================================================== */

const BooksSection: React.FC<{
  books: Book[];
  view: 'grid' | 'list';
  reset: () => void;
  isFetching: boolean;
  error: string | null;
  retry: () => void;
  sentinelRef: React.RefObject<HTMLDivElement>;
  onBookClick: (id: number) => void;
}> = ({ books, view, reset, isFetching, error, retry, sentinelRef, onBookClick }) => (
  <section className="max-w-[1280px] mx-auto px-5 md:px-8 py-12 md:py-14">
    {error && books.length === 0 ? (
      <div className="text-center py-20">
        <p className="text-[var(--ink-soft)] mb-5">{error}</p>
        <button
          onClick={retry}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[var(--ink)] text-[var(--paper)] text-[13px] font-medium hover:bg-[var(--primary)] active:translate-y-px transition-all"
        >
          Try again
        </button>
      </div>
    ) : books.length === 0 && isFetching ? (
      <BookGridSkeleton />
    ) : books.length === 0 ? (
      <EmptyState reset={reset} />
    ) : view === 'grid' ? (
      <BookGrid books={books} onBookClick={onBookClick} />
    ) : (
      <BookList books={books} onBookClick={onBookClick} />
    )}

    {/* Infinite scroll sentinel */}
    <div ref={sentinelRef} className="h-1" />

    {/* Next-page loading indicator */}
    {isFetching && books.length > 0 && (
      <div className="flex items-center justify-center gap-3 py-8 text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--muted)]">
        <span className="w-4 h-4 rounded-full border-2 border-[var(--border)] border-t-[var(--gold)] animate-spin" />
        Loading more
      </div>
    )}

    {/* Per-page error when books already loaded */}
    {error && books.length > 0 && (
      <div className="flex justify-center items-center gap-3 py-6 text-[13px] text-[var(--ink-soft)]">
        <span>{error}</span>
        <button onClick={retry} className="underline hover:text-[var(--ink)] transition-colors">Retry</button>
      </div>
    )}
  </section>
);

/* Keyboard + click handlers for tiles and rows (they open the drawer). */
const pressable = (open: () => void) => ({
  role: 'button' as const,
  tabIndex: 0,
  onClick: open,
  onKeyDown: (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      open();
    }
  },
});

/* =====================================================================
 * GRID VIEW: naked covers on the shelf, no card boxes.
 * ===================================================================== */

const BookGrid: React.FC<{ books: Book[]; onBookClick: (id: number) => void }> = ({ books, onBookClick }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-5 gap-y-9 md:gap-x-6 md:gap-y-11">
    {books.map((b, i) => (
      <motion.article
        key={b.id}
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.1 }}
        transition={{ duration: 0.4, delay: Math.min(i % 20, 8) * 0.04, ease: 'easeOut' }}
        {...pressable(() => onBookClick(b.id))}
        aria-label={`${b.title}, view details`}
        className="group cursor-pointer outline-offset-4"
      >
        <BookCover
          book={b}
          className="aspect-[2/3] w-full border border-black/10 shadow-[0_20px_36px_-22px_rgba(20,24,43,0.4)] transition-transform duration-300 group-hover:-translate-y-1.5"
        />
        <div className="mt-3.5 flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="font-display font-bold text-[15.5px] leading-[1.2] tracking-tight line-clamp-2 text-[var(--ink)] group-hover:text-[var(--primary)] transition-colors">
              {b.title}
            </h3>
            <p className="mt-0.5 font-serif italic text-[12.5px] text-[var(--ink-soft)] truncate pb-0.5">
              {authorLine(b)}
            </p>
          </div>
          <ArrowUpRight
            className="w-4 h-4 mt-1 text-[var(--muted)] shrink-0 opacity-0 group-hover:opacity-100 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all"
            strokeWidth={1.75}
          />
        </div>
        <div className="mt-1.5 flex items-center justify-between gap-2">
          <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-[var(--muted)] truncate">
            {b.genres?.[0]?.name ?? 'General'}
          </span>
          <AvailabilityBadge available={b.available_copies ?? 0} total={b.total_copies ?? 0} />
        </div>
      </motion.article>
    ))}
  </div>
);

/* =====================================================================
 * LIST VIEW: the card-catalog ledger, one header row and clean entries.
 * ===================================================================== */

const BookList: React.FC<{ books: Book[]; onBookClick: (id: number) => void }> = ({ books, onBookClick }) => (
  <div>
    {/* Header row (desktop only) */}
    <div className="hidden sm:grid grid-cols-12 gap-4 md:gap-6 pb-3 text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--muted)]">
      <span className="col-span-1" />
      <span className="col-span-5">Title</span>
      <span className="col-span-2">Genre</span>
      <span className="col-span-1">Year</span>
      <span className="col-span-2">Copies</span>
      <span className="col-span-1" />
    </div>
    <div className="border-t border-[var(--border)]">
      {books.map((b, i) => (
        <motion.div
          key={b.id}
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.4, delay: Math.min(i % 20, 8) * 0.03, ease: 'easeOut' }}
          {...pressable(() => onBookClick(b.id))}
          aria-label={`${b.title}, view details`}
          className="group grid grid-cols-12 gap-4 md:gap-6 py-4 items-center border-b border-[var(--border)] hover:bg-[var(--surface)]/70 px-3 -mx-3 transition-colors cursor-pointer"
        >
          <div className="col-span-2 sm:col-span-1">
            <BookCover book={b} className="aspect-[2/3] w-full max-w-[56px]" />
          </div>

          <div className="col-span-7 sm:col-span-5 min-w-0">
            <h3 className="font-display font-bold text-[1.05rem] md:text-[1.15rem] leading-tight tracking-tight truncate group-hover:text-[var(--primary)] transition-colors">
              {b.title}
            </h3>
            <p className="font-serif italic text-[var(--ink-soft)] text-[13px] truncate pb-0.5">
              {authorLine(b)}
            </p>
          </div>

          <div className="hidden sm:block col-span-2 text-[12.5px] text-[var(--ink-soft)] truncate">
            {b.genres?.[0]?.name ?? 'General'}
          </div>

          <div className="hidden sm:block col-span-1 text-[12.5px] font-mono tabular-nums text-[var(--ink-soft)]">
            {b.publication_date?.slice(0, 4) ?? ''}
          </div>

          <div className="col-span-3 sm:col-span-2">
            <AvailabilityBadge available={b.available_copies ?? 0} total={b.total_copies ?? 0} long />
          </div>

          <div className="hidden sm:flex col-span-1 justify-end">
            <ArrowUpRight
              className="w-4 h-4 text-[var(--muted)] group-hover:text-[var(--ink)] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all"
              strokeWidth={1.75}
            />
          </div>
        </motion.div>
      ))}
    </div>
  </div>
);

/* =====================================================================
 * SKELETON: matches the naked-cover grid shape.
 * ===================================================================== */

const BookGridSkeleton: React.FC = () => (
  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-5 gap-y-9 md:gap-x-6 md:gap-y-11">
    {Array.from({ length: 8 }).map((_, i) => (
      <div key={i} className="animate-pulse">
        <div className="aspect-[2/3] w-full rounded-md bg-[var(--surface-2)]" />
        <div className="mt-3.5 space-y-2">
          <div className="h-4 w-full rounded bg-[var(--surface-2)]" />
          <div className="h-3 w-3/5 rounded bg-[var(--surface-2)]" />
        </div>
      </div>
    ))}
  </div>
);

/* =====================================================================
 * EMPTY STATE
 * ===================================================================== */

const EmptyState: React.FC<{ reset: () => void }> = ({ reset }) => (
  <div className="text-center py-24">
    <h3 className="font-display font-[900] text-[1.9rem] tracking-[-0.02em]">
      Nothing on <span className="font-serif italic font-normal text-[var(--primary)]">this</span> shelf.
    </h3>
    <p className="mt-3 text-[var(--ink-soft)] max-w-[42ch] mx-auto text-[14.5px] leading-[1.6]">
      No titles match those filters. Try widening the genre or turning off the shelf-only filter.
    </p>
    <button
      onClick={reset}
      className="mt-7 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[var(--ink)] text-[var(--paper)] text-[13px] font-medium hover:bg-[var(--primary)] active:translate-y-px transition-all"
    >
      <X className="w-3.5 h-3.5" strokeWidth={1.75} /> Clear all filters
    </button>
  </div>
);

/* =====================================================================
 * DETAIL DRAWER: logic preserved (lazy fetch, retry, reserve flow).
 * ===================================================================== */

const BookDetailDrawer: React.FC<{ bookId: number | null; onClose: () => void }> = ({ bookId, onClose }) => {
  const isOpen = bookId !== null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm"
          />

          {/* Drawer */}
          <motion.aside
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 bottom-0 z-50 w-full max-w-[480px] bg-[var(--bg)] border-l border-[var(--border)] shadow-[-24px_0_60px_-30px_rgba(10,18,40,0.4)] overflow-y-auto flex flex-col"
          >
            {/* Header */}
            <div className="sticky top-0 z-10 bg-[var(--bg)]/90 backdrop-blur-md border-b border-[var(--border)] px-6 py-4 flex items-center justify-between">
              <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-[var(--muted)]">From the shelf</span>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-[var(--muted)] hover:text-[var(--ink)] hover:bg-[var(--surface-2)] transition-colors"
                aria-label="Close"
              >
                <X className="w-4 h-4" strokeWidth={1.75} />
              </button>
            </div>

            <DrawerContent key={bookId} bookId={bookId} />
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

/* Content is keyed by bookId so every book opens with fresh reserve
   state; the fetch itself is React Query (cached, retryable). */
const DrawerContent: React.FC<{ bookId: number }> = ({ bookId }) => {
  const isAuthenticated = TokenManager.isTokenValid();
  const [reserving, setReserving] = useState(false);
  const [reserveSuccess, setReserveSuccess] = useState(false);
  const [reserveError, setReserveError] = useState<string | null>(null);

  const { data: book, isLoading: loading, isError, refetch } = useQuery({
    queryKey: ['catalog', 'book', bookId],
    queryFn: () => booksApi.getBook(bookId),
    staleTime: 60 * 1000,
  });

  return (
    <div className="flex-1 px-6 py-6">
              {loading && (
                <div className="animate-pulse space-y-5">
                  <div className="aspect-[2/3] w-[150px] rounded-md bg-[var(--surface-2)]" />
                  <div className="h-6 w-3/4 rounded bg-[var(--surface-2)]" />
                  <div className="h-4 w-1/2 rounded bg-[var(--surface-2)]" />
                  <div className="space-y-2">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="h-3 rounded bg-[var(--surface-2)]" />
                    ))}
                  </div>
                </div>
              )}

              {isError && !loading && (
                <div className="text-center py-12">
                  <p className="text-[var(--ink-soft)] mb-4">Failed to load book details.</p>
                  <button
                    onClick={() => refetch()}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[var(--ink)] text-[var(--paper)] text-[13px] font-medium hover:bg-[var(--primary)] active:translate-y-px transition-all"
                  >
                    Retry
                  </button>
                </div>
              )}

              {book && !loading && (
                <div className="space-y-6">
                  {/* Cover + identity */}
                  <div className="flex gap-5">
                    <BookCover
                      book={book}
                      className="aspect-[2/3] w-[120px] shrink-0 border border-black/10 shadow-[0_20px_36px_-22px_rgba(20,24,43,0.45)]"
                    />
                    <div className="min-w-0 pt-1">
                      <h2 className="font-display font-[900] text-[1.4rem] leading-[1.1] tracking-[-0.01em] text-[var(--ink)]">
                        {book.full_title ?? book.title}
                      </h2>
                      <p className="mt-1.5 font-serif italic text-[var(--ink-soft)] text-[15px] pb-0.5">
                        {authorLine(book)}
                      </p>
                      {book.average_rating && parseFloat(book.average_rating) > 0 && (
                        <div className="mt-2.5 inline-flex items-center gap-1.5 font-mono text-[12px] text-[var(--gold-text)]">
                          <Star className="w-3.5 h-3.5 fill-current" strokeWidth={0} />
                          {parseFloat(book.average_rating).toFixed(1)}
                          {book.total_reviews ? (
                            <span className="text-[var(--muted)]"> · {book.total_reviews} reviews</span>
                          ) : null}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Genres */}
                  {book.genres && book.genres.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {book.genres.map((g) => (
                        <span key={g.id} className="px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-[0.12em] bg-[var(--surface-2)] text-[var(--ink-soft)]">
                          {g.name}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Availability */}
                  <div className="flex items-center gap-2.5 py-3 border-y border-[var(--border)]">
                    <span
                      className={`w-2 h-2 rounded-full shrink-0 ${
                        (book.available_copies ?? 0) > 0 ? 'bg-[var(--gold)]' : 'bg-[var(--danger)]'
                      }`}
                    />
                    <span className="text-[13.5px] text-[var(--ink-soft)]">
                      {(book.available_copies ?? 0) > 0 ? (
                        <>
                          <span className="font-semibold text-[var(--ink)] tabular-nums">{book.available_copies}</span> of{' '}
                          <span className="font-semibold text-[var(--ink)] tabular-nums">{book.total_copies ?? 0}</span> copies on the shelf
                        </>
                      ) : (
                        'All copies are out right now'
                      )}
                    </span>
                  </div>

                  {/* Meta grid */}
                  <div className="grid grid-cols-2 gap-x-4 gap-y-4 text-[13px]">
                    {book.publication_date && (
                      <div>
                        <div className="font-mono uppercase tracking-[0.14em] text-[10px] text-[var(--muted)] mb-1">Published</div>
                        <div className="text-[var(--ink-soft)] font-mono tabular-nums">{book.publication_date.slice(0, 4)}</div>
                      </div>
                    )}
                    {book.language && (
                      <div>
                        <div className="font-mono uppercase tracking-[0.14em] text-[10px] text-[var(--muted)] mb-1">Language</div>
                        <div className="text-[var(--ink-soft)] capitalize">{book.language}</div>
                      </div>
                    )}
                    {book.pages && (
                      <div>
                        <div className="font-mono uppercase tracking-[0.14em] text-[10px] text-[var(--muted)] mb-1">Pages</div>
                        <div className="text-[var(--ink-soft)] font-mono tabular-nums">{book.pages}</div>
                      </div>
                    )}
                    {book.isbn && (
                      <div>
                        <div className="font-mono uppercase tracking-[0.14em] text-[10px] text-[var(--muted)] mb-1">ISBN</div>
                        <div className="text-[var(--ink-soft)] font-mono text-[12px]">{book.isbn}</div>
                      </div>
                    )}
                  </div>

                  {/* Description */}
                  {book.description && (
                    <div className="pt-1">
                      <div className="font-mono uppercase tracking-[0.14em] text-[10px] text-[var(--muted)] mb-2">About</div>
                      <p className="text-[13.5px] text-[var(--ink-soft)] leading-[1.65] line-clamp-6">{book.description}</p>
                    </div>
                  )}

                  {/* Reserve action */}
                  {isAuthenticated ? (
                    <div className="pt-2">
                      {reserveSuccess ? (
                        <div className="flex items-center gap-2.5 px-4 py-3 rounded-lg bg-[var(--success)]/10 border border-[var(--success)]/25 text-[var(--success)] text-[13px] font-medium">
                          <span className="w-2 h-2 rounded-full bg-[var(--success)] shrink-0" />
                          Request submitted. The librarian will confirm shortly.
                        </div>
                      ) : (
                        <>
                          <button
                            onClick={async () => {
                              if (!book || reserving) return;
                              setReserving(true);
                              setReserveError(null);
                              try {
                                await loansApi.createBorrowRequest({ book: book.id });
                                setReserveSuccess(true);
                              } catch {
                                setReserveError('Could not submit request. Please try again.');
                              } finally {
                                setReserving(false);
                              }
                            }}
                            disabled={reserving}
                            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-[var(--ink)] text-[var(--paper)] text-[13.5px] font-semibold hover:bg-[var(--primary)] active:translate-y-px transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                          >
                            {reserving ? (
                              <span className="w-4 h-4 rounded-full border-2 border-[var(--paper)]/30 border-t-[var(--paper)] animate-spin" />
                            ) : (
                              <BookMarked className="w-4 h-4" strokeWidth={1.75} />
                            )}
                            {reserving ? 'Submitting request' : 'Request to borrow'}
                          </button>
                          {reserveError && (
                            <p className="mt-2 text-[12px] text-[var(--danger)] text-center">{reserveError}</p>
                          )}
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="pt-2">
                      <Link
                        to="/signin"
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-[var(--ink)] text-[var(--paper)] text-[13.5px] font-semibold hover:bg-[var(--primary)] active:translate-y-px transition-all"
                      >
                        Sign in
                      </Link>
                      <p className="mt-2.5 text-[12px] text-[var(--muted)] text-center">
                        You need your SMCHS account to request books.
                      </p>
                    </div>
                  )}
                </div>
              )}
    </div>
  );
};

export default Catalog;
